import { CATEGORIES, todayStr } from "../../constants/categories";

const TEAL = "#14B8A6";
const TEAL_LIGHT = "#2DD4BF";
const TEAL_DIM = "#0D9488";
const CYAN = "#06B6D4";

const pad = n => String(n).padStart(2, "0");
const dayNames = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];

function getWeekNumber(dateStr) {
  const d = new Date(dateStr);
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
}

function groupByWeek(entries) {
  const weeks = {};
  entries.forEach(e => {
    const wk = getWeekNumber(e.date);
    const year = e.date.substring(0, 4);
    const key = `${year}-W${wk}`;
    if (!weeks[key]) weeks[key] = { key, entries: [], weekNum: wk, year: parseInt(year) };
    weeks[key].entries.push(e);
  });
  return Object.values(weeks)
    .sort((a, b) => b.entries[0].date.localeCompare(a.entries[0].date));
}

function getWeekRange(entries) {
  const dates = entries.map(e => e.date).sort();
  const first = new Date(dates[0]);
  const last = new Date(dates[dates.length - 1]);
  return `${pad(first.getDate())}/${pad(first.getMonth() + 1)} — ${pad(last.getDate())}/${pad(last.getMonth() + 1)}`;
}

function getMilestone(completedSoFar) {
  if (completedSoFar === 1) return { icon: "🎯", text: "Bước đầu tiên!" };
  if (completedSoFar === 7) return { icon: "🔥", text: "7 ngày!" };
  if (completedSoFar === 14) return { icon: "💎", text: "14 ngày!" };
  if (completedSoFar === 30) return { icon: "🏆", text: "30 ngày!" };
  if (completedSoFar === 50) return { icon: "⚡", text: "50 ngày!" };
  if (completedSoFar === 100) return { icon: "👑", text: "100 ngày!" };
  return null;
}

export default function TimelineStyle({
  histMap, history, completedCount, streak, completionRate,
  calMonth, calYear, monthDays, firstDayOfWeek, monthNames,
  prevMonth, nextMonth, isCurrentMonth,
  selectedDay, setSelectedDay,
}) {
  // Sort history by date ascending for milestone tracking
  const sortedCompleted = [...history]
    .filter(h => h.completed)
    .sort((a, b) => a.date.localeCompare(b.date));

  // Build milestone map: date -> milestone
  const milestoneMap = {};
  sortedCompleted.forEach((h, i) => {
    const m = getMilestone(i + 1);
    if (m) milestoneMap[h.date] = m;
  });

  // Group all entries by week (most recent first)
  const allEntries = [...history].sort((a, b) => b.date.localeCompare(a.date));
  const rawWeeks = groupByWeek(allEntries);

  // Pad to always show 8 weeks (fill empty weeks going back from current)
  const weeksToShow = 8;
  const now = new Date();
  const paddedWeeks = [];
  for (let i = 0; i < weeksToShow; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    const wk = getWeekNumber(d.toISOString().split("T")[0]);
    const year = d.getFullYear();
    const key = `${year}-W${wk}`;
    const existing = rawWeeks.find(w => w.key === key);
    paddedWeeks.push(existing || { key, entries: [], weekNum: wk, year });
  }
  const weeks = paddedWeeks;

  // Compute weekly stats
  const totalWeeks = rawWeeks.length;
  const perfectWeeks = rawWeeks.filter(w => {
    const completed = w.entries.filter(e => e.completed).length;
    return completed === w.entries.length && w.entries.length >= 5;
  }).length;

  // Current streak "chapter" title
  const streakNum = streak.current_streak || 0;
  const chapterTitle = streakNum >= 100 ? "Chương Huyền Thoại"
    : streakNum >= 30 ? "Chương Bứt Phá"
    : streakNum >= 7 ? "Chương Kiên Trì"
    : streakNum >= 1 ? "Chương Khởi Đầu"
    : "Mở Đầu Hành Trình";

  return (
    <>
      {/* Journey Header */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "20px",
        padding: "24px 20px",
        marginBottom: "20px",
        position: "relative",
        overflow: "hidden",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 30% 20%, ${TEAL}08, transparent 60%)`, pointerEvents: "none" }} />

        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          <div style={{ fontSize: "11px", color: TEAL, fontWeight: "600", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "4px" }}>
            📜 {chapterTitle}
          </div>
          <div style={{ fontSize: "22px", fontWeight: "900", color: "var(--text-primary)" }}>
            Hành Trình Của Bạn
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-tertiary)", marginTop: "4px" }}>
            {completedCount} nhiệm vụ · {totalWeeks} tuần · {perfectWeeks > 0 ? `${perfectWeeks} tuần hoàn hảo` : "Đang bắt đầu"}
          </div>
        </div>

        {/* Journey progress bar */}
        <div style={{ maxWidth: "280px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: "var(--text-muted)", marginBottom: "6px" }}>
            <span>Ngày 1</span>
            <span style={{ color: TEAL, fontWeight: "700" }}>{completedCount} ngày</span>
            <span>365</span>
          </div>
          <div style={{ background: "var(--bg-inset)", borderRadius: "10px", height: "8px", overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: "10px",
              background: `linear-gradient(90deg, ${TEAL_DIM}, ${TEAL}, ${CYAN})`,
              width: `${Math.min((completedCount / 365) * 100, 100)}%`,
              transition: "width 1s ease",
            }} />
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "16px" }}>
          {[
            { value: `${streakNum}🔥`, label: "Streak", color: TEAL },
            { value: completedCount, label: "Hoàn thành", color: TEAL_DIM },
            { value: streak.longest_streak || 0, label: "Kỷ lục", color: "#D97706" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "18px", fontWeight: "900", color: s.color }}>{s.value}</div>
              <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Activity Bars */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>📊</span> Nhịp Độ Gần Đây
        </div>
        {(() => {
          const maxBarHeight = 64;
          const allCompleted = [...weeks].map(w => w.entries.filter(e => e.completed).length);
          const maxVal = Math.max(1, ...allCompleted);
          return (
            <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", height: `${maxBarHeight + 30}px` }}>
              {[...weeks].reverse().map((w, i) => {
                const completed = w.entries.filter(e => e.completed).length;
                const barH = completed > 0 ? Math.max((completed / maxVal) * maxBarHeight, 12) : 3;
                const isCurrentWeek = i === weeks.length - 1;
                return (
                  <div key={w.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                    <div style={{ fontSize: "9px", color: TEAL_LIGHT, fontWeight: "700" }}>{completed > 0 ? completed : ""}</div>
                    <div style={{
                      width: "100%",
                      height: `${barH}px`,
                      borderRadius: "4px 4px 2px 2px",
                      background: completed > 0
                        ? `linear-gradient(180deg, ${TEAL}, ${TEAL_DIM})`
                        : "var(--border-secondary)",
                      opacity: isCurrentWeek ? 1 : 0.5 + (i * 0.07),
                      transition: "height 0.5s ease",
                      boxShadow: completed > 0 ? `0 0 6px ${TEAL}33` : "none",
                    }} />
                    <div style={{ fontSize: "8px", color: "var(--text-muted)" }}>W{w.weekNum}</div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Timeline */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "16px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>📖</span> Dòng Thời Gian
        </div>

        <div style={{ position: "relative", paddingLeft: "24px" }}>
          {/* Vertical line */}
          <div style={{
            position: "absolute", left: "7px", top: "4px", bottom: "4px",
            width: "2px", background: `linear-gradient(180deg, ${TEAL}, ${TEAL}33, transparent)`,
            borderRadius: "1px",
          }} />

          {rawWeeks.slice(0, 6).map((week, wi) => {
            const weekCompleted = week.entries.filter(e => e.completed);
            const weekTotal = week.entries.length;
            const cats = [...new Set(weekCompleted.map(e => e.category_id))];
            const dateRange = getWeekRange(week.entries);
            const hasMilestone = week.entries.some(e => milestoneMap[e.date]);
            const milestone = week.entries.find(e => milestoneMap[e.date]);

            return (
              <div key={week.key} style={{ marginBottom: wi < 5 ? "20px" : 0, position: "relative" }}>
                {/* Timeline dot */}
                <div style={{
                  position: "absolute", left: "-20px", top: "2px",
                  width: "12px", height: "12px", borderRadius: "50%",
                  background: weekCompleted.length > 0 ? TEAL : "var(--border-secondary)",
                  border: `2px solid ${weekCompleted.length > 0 ? TEAL_LIGHT : "var(--border-primary)"}`,
                  boxShadow: weekCompleted.length > 0 ? `0 0 8px ${TEAL}44` : "none",
                  boxSizing: "border-box",
                }} />

                {/* Milestone badge */}
                {hasMilestone && milestone && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    background: `linear-gradient(135deg, #F59E0B22, ${TEAL}15)`,
                    border: "1px solid #F59E0B44",
                    borderRadius: "10px",
                    padding: "2px 10px",
                    marginBottom: "6px",
                    fontSize: "10px", fontWeight: "700", color: "#F59E0B",
                  }}>
                    {milestoneMap[milestone.date].icon} {milestoneMap[milestone.date].text}
                  </div>
                )}

                {/* Week header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", color: "var(--text-secondary)" }}>
                    Tuần {week.weekNum}
                  </span>
                  <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>{dateRange}</span>
                </div>

                {/* Week summary */}
                <div style={{
                  background: "var(--bg-inset)",
                  borderRadius: "10px",
                  padding: "12px",
                  borderLeft: `3px solid ${weekCompleted.length > 0 ? TEAL : "var(--border-secondary)"}`,
                }}>
                  {/* Completion bar */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <div style={{ flex: 1, background: "var(--border-secondary)", borderRadius: "4px", height: "4px", overflow: "hidden" }}>
                      <div style={{
                        height: "100%", borderRadius: "4px",
                        background: `linear-gradient(90deg, ${TEAL}, ${CYAN})`,
                        width: `${weekTotal > 0 ? (weekCompleted.length / weekTotal) * 100 : 0}%`,
                        transition: "width 0.5s",
                      }} />
                    </div>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: TEAL_LIGHT }}>
                      {weekCompleted.length}/{weekTotal}
                    </span>
                  </div>

                  {/* Category dots */}
                  {cats.length > 0 && (
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
                      {cats.map(catId => {
                        const cat = CATEGORIES.find(c => c.id === catId);
                        if (!cat) return null;
                        return (
                          <span key={catId} style={{
                            fontSize: "10px",
                            background: `${cat.color}18`,
                            color: cat.color,
                            padding: "2px 8px",
                            borderRadius: "8px",
                            fontWeight: "600",
                          }}>
                            {cat.emoji} {cat.label}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Day entries */}
                  {week.entries.slice(0, 5).map(entry => {
                    const cat = CATEGORIES.find(c => c.id === entry.category_id);
                    const d = new Date(entry.date);
                    const dayName = dayNames[d.getDay()];
                    const items = histMap[entry.date] || [];
                    return (
                      <div key={entry.date + entry.id}
                        onClick={() => items.length > 0 && setSelectedDay(items)}
                        style={{
                          display: "flex", alignItems: "center", gap: "8px",
                          padding: "5px 0",
                          cursor: items.length > 0 ? "pointer" : "default",
                          borderBottom: "1px solid var(--border-secondary)",
                        }}>
                        <span style={{
                          width: "6px", height: "6px", borderRadius: "50%",
                          background: entry.completed ? (cat?.color || TEAL) : "var(--text-muted)",
                          flexShrink: 0,
                        }} />
                        <span style={{ fontSize: "10px", color: "var(--text-muted)", minWidth: "44px" }}>
                          {dayName.replace("Thứ ", "T")}
                        </span>
                        <span style={{
                          fontSize: "11px",
                          color: entry.completed ? "var(--text-secondary)" : "var(--text-muted)",
                          textDecoration: entry.completed ? "none" : "line-through",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {entry.title || "Không có thử thách"}
                        </span>
                        {entry.completed && (
                          <span style={{ fontSize: "10px", color: TEAL, flexShrink: 0 }}>✓</span>
                        )}
                      </div>
                    );
                  })}
                  {week.entries.length > 5 && (
                    <div style={{ fontSize: "10px", color: "var(--text-muted)", textAlign: "center", marginTop: "6px" }}>
                      +{week.entries.length - 5} ngày khác
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {rawWeeks.length > 6 && (
            <div style={{ textAlign: "center", paddingTop: "8px" }}>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                ...và {rawWeeks.length - 6} tuần trước đó
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Monthly Calendar */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <button onClick={prevMonth} style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", fontSize: "20px", padding: "4px 10px" }}>‹</button>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" }}>{monthNames[calMonth]} {calYear}</div>
          <button onClick={nextMonth} style={{ background: "none", border: "none", color: isCurrentMonth ? "var(--border-primary)" : "var(--text-tertiary)", cursor: isCurrentMonth ? "default" : "pointer", fontSize: "20px", padding: "4px 10px" }}>›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "6px", textAlign: "center" }}>
          {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map(d => (
            <div key={d} style={{ fontSize: "11px", color: "var(--text-muted)", padding: "6px 0", fontWeight: "700" }}>{d}</div>
          ))}
          {Array.from({ length: (firstDayOfWeek + 6) % 7 }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: monthDays }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${calYear}-${pad(calMonth + 1)}-${pad(day)}`;
            const items = histMap[dateStr] || [];
            const hasCompleted = items.some(c => c.completed);
            const hasData = items.length > 0;
            const isToday = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            const isSelected = Array.isArray(selectedDay) && selectedDay[0]?.date === dateStr;
            const hasMilestoneDay = milestoneMap[dateStr];

            return (
              <div key={day} onClick={() => !isFuture && hasData && setSelectedDay(items)}
                style={{
                  aspectRatio: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "10px",
                  cursor: hasData && !isFuture ? "pointer" : "default",
                  background: isSelected ? `${TEAL}25` : hasCompleted ? `${TEAL}18` : "transparent",
                  border: isToday ? `2px solid ${TEAL}` : isSelected ? `2px solid ${TEAL}88` : "1.5px solid transparent",
                  boxSizing: "border-box",
                  transition: "all 0.2s",
                  position: "relative",
                }}>
                {hasMilestoneDay ? (
                  <span style={{ fontSize: "14px" }}>{hasMilestoneDay.icon}</span>
                ) : hasCompleted ? (
                  <span style={{ fontSize: "12px", fontWeight: "700", color: TEAL }}>{day}</span>
                ) : (
                  <span style={{
                    fontSize: "12px",
                    fontWeight: isToday ? "800" : "500",
                    color: isFuture ? "var(--border-primary)" : isToday ? TEAL : hasData ? "var(--text-tertiary)" : "var(--text-muted)",
                  }}>{day}</span>
                )}
                {hasCompleted && !hasMilestoneDay && (
                  <div style={{
                    position: "absolute", bottom: "3px",
                    width: "4px", height: "4px", borderRadius: "50%",
                    background: TEAL,
                  }} />
                )}
                {isToday && !hasCompleted && (
                  <div style={{
                    position: "absolute", bottom: "3px",
                    width: "4px", height: "4px", borderRadius: "50%",
                    background: TEAL, animation: "pulse 2s infinite",
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
